/**
 * Online / offline, as one reactive source.
 *
 * Three signals feed it: `navigator.onLine`, the window's `online` / `offline`
 * events, and (inside the desktop app) the shell's own network check via the
 * chrome metrics — the shell notices a dead connection that the page's
 * `navigator.onLine` still reports as up (captive portals, VPN drops).
 * Offline when any of them says so.
 *
 * Works on the web too (it is just the browser's signal there), but only the
 * desktop app shows UI for it today; the HTTP client reads `isOfflineNow()` to
 * tell a network failure from a server error.
 */
import { computed, readonly, ref, watch } from 'vue'
import { setBrowserOffline, useDesktopChrome } from './useDesktopChrome'

const browserOnline = ref(typeof navigator === 'undefined' ? true : navigator.onLine !== false)
const lastOnlineAt = ref<number | null>(browserOnline.value ? Date.now() : null)
let listening = false

function onOnline() {
    browserOnline.value = true
    lastOnlineAt.value = Date.now()
    setBrowserOffline(false)
}

function onOffline() {
    browserOnline.value = false
    setBrowserOffline(true)
}

function ensureListening() {
    if (listening || typeof window === 'undefined') return
    listening = true
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
}

/** Pure: offline when the browser or the shell says so. */
export function computeOffline(browserIsOnline: boolean, shellOffline: boolean | null | undefined): boolean {
    return !browserIsOnline || shellOffline === true
}

const chrome = useDesktopChrome()
const isOffline = computed(() => computeOffline(browserOnline.value, chrome.metrics.value?.offline))

// Track the moment we were last known to be online, whichever signal recovered.
watch(isOffline, (offline, wasOffline) => {
    if (wasOffline && !offline) lastOnlineAt.value = Date.now()
})

/** Synchronous read for non-component code (HTTP interceptors). */
export function isOfflineNow(): boolean {
    ensureListening()
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return true
    return isOffline.value
}

/** Test hook: simulate the browser signal. */
export function setBrowserOnlineForTest(online: boolean): void {
    if (online) onOnline()
    else onOffline()
}

export function useConnectivity() {
    ensureListening()
    return {
        isOffline,
        isOnline: computed(() => !isOffline.value),
        /** ms timestamp of the last time the app was known to be online (null if never this session). */
        lastOnlineAt: readonly(lastOnlineAt),
    }
}

export default useConnectivity
